import { Store } from '@website/Store';
import { GroupForBookingFormQuery } from '@website/types/graphqlOperations';
import { DateTime, Duration } from 'luxon';
import { useContext, useEffect, useMemo } from 'react';
import { Alert, Form, Table } from 'react-bootstrap';
import { UseFormReturn } from 'react-hook-form';
import { FaRegClock } from 'react-icons/fa';
import PriceFormat from '../../utilities/PriceFormat';
import { BookingFormOnSubmitValues } from '../BookingForm';
import CheckoutGuestsOnGroup from './CheckoutGuestsOnGroup';

/**
 * Calculate if the balance is due
 *
 * @param startDate Group start date
 */
export function isBalanceDue(startDate: string) {
  // Get departure date
  const departure = DateTime.fromISO(startDate);

  // Compare again 2 months + 7 days
  const twoMonths = Duration.fromObject({ months: 2 });
  const sevenDays = Duration.fromObject({ days: 7 });
  const nowPlusTwoMonths = DateTime.utc().plus(twoMonths).plus(sevenDays);

  // Return true if the departure is less than 2 months away
  return departure < nowPlusTwoMonths;
}

/**
 * Calculate balance due date
 *
 * @param startDate Group start date
 */
export function calculateBalanceDueDate(startDate: string, format?: string) {
  const departure = DateTime.fromISO(startDate);

  // Compare again 2 months + 7 days
  const twoMonths = Duration.fromObject({ months: 2 });

  if (typeof format === 'string') {
    return departure.minus(twoMonths).toFormat(format);
  }

  // Return in DATE_MED format (e.g. Oct 14, 1983 in the US)
  return departure.minus(twoMonths).toLocaleString(DateTime.DATE_MED);
}

/**
 * Calculate the total credit to apply
 *
 * @param maxDeduction The group max deduction (maximum credit it allows per space)
 * @param spacesRequired  Number of spaces required
 * @param customerCreditBalance Customer total credit balance in the correct currency
 */
export function calculateTotalCredit(
  maxCredit: number,
  spacesRequired: number,
  customerCreditBalance: number,
) {
  return Math.max(
    Math.min(maxCredit * spacesRequired, customerCreditBalance),
    0,
  );
}

/**
 * Calculate the total price after credit
 *
 * @param groupUnitPrice Group unit price (ZD)
 * @param totalCredit Total credit spend (ZD)
 * @param spacesRequired Spaces required
 */
export function calculateTotal(
  groupUnitPrice: number,
  totalCredit: number,
  spacesRequired: number,
) {
  return groupUnitPrice * spacesRequired - totalCredit;
}

/**
 * Calculate the total balance due later (assuming it's not all paid now)
 *
 * @param groupUnitPrice Group unit price (ZD)
 * @param groupUnitDeposit Group unit deposit (ZD)
 * @param spacesRequired Spaces required
 * @param totalCredit Total credit spend (ZD)
 */
export function calculateTotalBalanceDueLater(
  groupUnitPrice: number,
  groupUnitDeposit: number,
  spacesRequired: number,
  totalCredit: number,
) {
  const greaterOfDepositOrCredit = Math.max(
    groupUnitDeposit * spacesRequired,
    totalCredit,
  );

  return groupUnitPrice * spacesRequired - greaterOfDepositOrCredit;
}

/**
 * Calculate the deposit due now (if paying just a deposit)
 *
 * @param groupUnitDeposit Group unit deposit (ZD)
 * @param spacesRequired Spaces required
 * @param totalCredit Total credit spend (ZD)
 */
export function calculateDepositDueNow(
  groupUnitDeposit: number,
  spacesRequired: number,
  totalCredit: number,
) {
  // Note credit can be greater than the deposit, so we max the deposit at 0 to avoid negative deposits due
  return Math.max(groupUnitDeposit * spacesRequired - totalCredit, 0);
}

/**
 * Spaces required <options>
 *
 * Creates options for the number of spaces possible to book
 */
export function SpacesOptions({ max }: { max: number }) {
  // Having lots of options (e.g. with a test group) can slow the form down, so we cap this at 100. We're unlikely to
  // have anyone booking that many spaces!
  const numberOfOptions = Math.min(max, 100);

  const output = [];
  for (let i = 1; i <= numberOfOptions; i++) {
    output.push(
      <option value={i} key={i}>
        {i}
      </option>,
    );
  }
  return <>{output}</>;
}

/**
 * Scarcity text
 *
 * Shows "Only X spaces remaining" if x <6
 */
export function Scarcity({ remainingSpaces }: { remainingSpaces: number }) {
  if (remainingSpaces < 6) {
    return (
      <div>
        <Alert variant="danger" className="badge fw-bold rounded-pill mb-4">
          <FaRegClock className="me-1 clock" />
          Only {remainingSpaces} space
          {remainingSpaces > 1 ? 's' : null} remaining
        </Alert>
      </div>
    );
  }

  return null;
}

export interface CheckoutPricesProps {
  customerCreditBalance: number;
  group: Omit<GroupForBookingFormQuery['group'], 'trip'>;
  register: UseFormReturn<BookingFormOnSubmitValues>['register'];
  setValue: UseFormReturn<BookingFormOnSubmitValues>['setValue'];
  watch: UseFormReturn<BookingFormOnSubmitValues>['watch'];
  hidePriceDetails?: boolean;
}

/**
 * Checkout Prices
 *
 * Exposes the price calculations (e.g. price * spaces -> total) in a table, including a field to change the number of
 * spaces. This field is given a react-hook-form name of `spacesRequired` and the component also exposes non-editable
 * fields for `credit.amountZD` and `payment.amountZD`.
 *
 * Note that `spacesRequired` is a select field, so the number seen by react-hook-form will be a string rather than
 * number.
 */
export default function CheckoutPrices({
  customerCreditBalance,
  group,
  register,
  setValue,
  watch,
  hidePriceDetails = false,
}: CheckoutPricesProps) {
  const { state } = useContext(Store);

  // State for spaces required (note select field needs string)
  const spacesRequiredString = watch('prices.spacesRequired', '1');
  const spacesRequired = parseInt(spacesRequiredString) || 1;

  // Is the balance due?
  const balanceDue = useMemo(() => isBalanceDue(group.start.utc), [group]);
  const showDeposit = !balanceDue;

  // Balance due date
  const balanceDueDate = useMemo(
    () => calculateBalanceDueDate(group.start.utc),
    [group],
  );

  // Calculate total credit spend
  const totalCredit = calculateTotalCredit(
    group.maxCredit.amountZD,
    spacesRequired,
    customerCreditBalance,
  );

  // Calculate total before credits
  const totalBeforeCredits = calculateTotal(
    group.guestPrice.amountZD,
    0,
    spacesRequired,
  );

  // Calculate total price
  const total = calculateTotal(
    group.guestPrice.amountZD,
    totalCredit,
    spacesRequired,
  );

  // Calculate the total balance
  const totalBalanceDueLater = calculateTotalBalanceDueLater(
    group.guestPrice.amountZD,
    group.guestDeposit.amountZD,
    spacesRequired,
    totalCredit,
  );

  // Calculate the deposit due now
  const depositDueNow = calculateDepositDueNow(
    group.guestDeposit.amountZD,
    spacesRequired,
    totalCredit,
  );

  // Set form credit/payment amounts (hidden fields)
  useEffect(() => {
    setValue('prices.credit', totalCredit.toString());
    setValue(
      'prices.payment',
      balanceDue ? total.toString() : depositDueNow.toString(),
    );
  }, [totalCredit, balanceDue, total, depositDueNow, setValue]);

  return (
    <>
      <div className="text-block pb-4">
        {state.isAuthenticated && (
          <>
            <h5>Number of Travellers</h5>
            <p className="text-sm text-muted">
              Please indicate how many people you would like to book for.
            </p>

            <Scarcity remainingSpaces={group.remainingSpaces} />
            <label htmlFor="spacesRequired" className="d-none">
              Spaces
            </label>
            <Form.Select
              id="spacesRequired"
              style={{
                width: 'auto',
                display: 'inline',
                marginBottom: 0,
              }}
              {...register('prices.spacesRequired')}
            >
              <SpacesOptions max={group.remainingSpaces} />
            </Form.Select>
          </>
        )}

        <CheckoutGuestsOnGroup guests={group.guests.items} />
      </div>

      {!hidePriceDetails && (
        <div className="text-block border-0">
          <h5>Price details</h5>

          <Table className="w-100">
            <tbody>
              <tr>
                <th className="fw-normal py-2 px-0">
                  <PriceFormat
                    amount={group.guestPrice.amountZD}
                    currencyIso={group.guestPrice.currency}
                  />{' '}
                  x {spacesRequired}
                </th>
                <td className="text-end py-2 ps-0">
                  {' '}
                  <PriceFormat
                    amount={totalBeforeCredits}
                    currencyIso={group.guestPrice.currency}
                  />
                </td>
              </tr>

              {totalCredit > 0 && (
                <tr>
                  <th className="fw-normal py-2 px-0">Credit</th>
                  <td className="text-end py-2 ps-0">
                    -
                    <PriceFormat
                      amount={totalCredit}
                      currencyIso={group.guestPrice.currency}
                    />
                  </td>
                </tr>
              )}
              <tr>
                <td className="fw-normal px-0 py-1">
                  Access exclusive gear and travel partner discounts
                </td>
                <td className="text-end py-1">Free</td>
              </tr>
              <tr className={showDeposit ? 'total' : 'fw-bold summary'}>
                <th
                  className={`py-2 px-0 ${
                    showDeposit ? 'fw-normal' : 'fw-bold'
                  }`}
                >
                  Total
                  {showDeposit ? '' : ' due now'}
                </th>
                <td className="text-end py-2 ps-0">
                  <PriceFormat
                    amount={total}
                    currencyIso={group.guestPrice.currency}
                  />
                </td>
              </tr>
              {showDeposit && (
                <>
                  <tr className="text-muted small balance">
                    <th className="fw-normal py-2 px-0">
                      <span>Balance due on {balanceDueDate}</span>
                    </th>
                    <td className="text-end py-2 ps-0">
                      <PriceFormat
                        amount={totalBalanceDueLater}
                        currencyIso={group.guestPrice.currency}
                      />
                    </td>
                  </tr>
                  <tr className="fw-bold summary">
                    <th className="py-2  px-0">Deposit due now</th>
                    <td className="text-end py-2 ps-0">
                      <PriceFormat
                        amount={depositDueNow}
                        currencyIso={group.guestPrice.currency}
                      />
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
}

/**
 * Helper interface for `onSubmit` handler
 *
 * Note HTML inputs all return strings not numbers!
 */
export interface CheckoutPricesSubmitValues {
  credit: string;
  payment: string;
  spacesRequired: string;
}
